import {
  LocallyDerivedTransformation,
  LocallyDerivedTransformationOperation,
} from '@ardoq/api-types';
import { Field } from '@ardoq/forms';
import { FieldType } from '@ardoq/renderers';
import { Select, SelectOption } from '@ardoq/select';
import { DEFAULT_ARDOQ_I18NLOCALE, I18nLocale, t } from '@ardoq/i18n';
import { EditorSectionProps } from './types';
import {
  concatPreset,
  dictionaryLookupPreset,
  localizeTransformationPreset,
  mathPreset,
  Preset,
  transformationPresets,
} from './transformationsPresets';

type OperationSectionProps = EditorSectionProps<
  LocallyDerivedTransformation | undefined
> & {
  i18nLocale?: I18nLocale;
};

export const OperationSection = ({
  type,
  value: transformation,
  onValueChange: onTransformationChange,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: OperationSectionProps) => {
  const handleOperationChange = (
    operation: LocallyDerivedTransformationOperation | null
  ) => {
    const preset = transformationPresets.find(
      preset => preset.transformation.operation === operation
    );

    onTransformationChange(preset?.transformation);
  };

  return (
    <Field
      label={t(
        i18nLocale,
        'scopeData:calculatedFields.operationLabel',
        'Operation'
      )}
    >
      <Select
        placeholder={t(
          i18nLocale,
          'scopeData:calculatedFields.operationPlaceholder',
          'Select operation'
        )}
        options={getPresetsForType(type)
          .map(preset => localizeTransformationPreset(preset, i18nLocale))
          .map(toOperationOption)}
        value={transformation?.operation}
        onValueChange={handleOperationChange}
        i18nLocale={i18nLocale}
      />
    </Field>
  );
};

const getPresetsForType = (type: FieldType): Preset[] => {
  switch (type) {
    case FieldType.TRANSFORM_NUMBER:
      return [dictionaryLookupPreset, mathPreset];
    case FieldType.TRANSFORM_TEXT:
      return [dictionaryLookupPreset, concatPreset];
    case FieldType.TRANSFORM_LIST:
      return [dictionaryLookupPreset];
    default:
      return [];
  }
};

const toOperationOption = (
  preset: Preset
): SelectOption<LocallyDerivedTransformationOperation> => ({
  label: preset.name,
  description: preset.description,
  leftIcon: { name: preset.icon },
  value: preset.transformation.operation,
});
